/** Hierarchy levels — Winner7 / Backend-UI ladder (CM = 1). */
export const ROLES = [
  'company-master',
  'triple-master',
  'double-master',
  'super-master',
  'master',
  'player',
] as const

export type Role = (typeof ROLES)[number]

export const ROLE_LABEL: Record<Role, string> = {
  'company-master': 'Company Master',
  'triple-master': 'Triple Master',
  'double-master': 'Double Master',
  'super-master': 'Super Master',
  master: 'Master',
  player: 'Member',
}

export const ROLE_SHORT: Record<Role, string> = {
  'company-master': 'CM',
  'triple-master': 'TM',
  'double-master': 'DM',
  'super-master': 'SM',
  master: 'M',
  player: 'MBR',
}

/** Members sit at level 6 — the leaf of the ladder. */
export const MEMBER_LEVEL = 6

export function levelToRole(level: number): Role {
  const map: Record<number, Role> = {
    1: 'company-master',
    2: 'triple-master',
    3: 'double-master',
    4: 'super-master',
    5: 'master',
    6: 'player',
  }
  return map[level] ?? 'master'
}

export function roleDepth(role: Role): number {
  return ROLES.indexOf(role)
}

/** CM creates only the next rung (TM). Members are created under agents, not from CM. */
export function creatableRoles(actor: Role): Role[] {
  if (actor === 'player') return []
  const next = ROLES[roleDepth(actor) + 1]
  return next ? [next] : []
}

/** Assignable on create: INR or POINTS (1 point = ₹100). POINT@1 is Lab-only parse. */
export const CURRENCY_TYPES = ['INR', 'POINTS', 'POINT@1'] as const
export type CurrencyType = (typeof CURRENCY_TYPES)[number]
export const ASSIGNABLE_CURRENCY_TYPES = ['INR', 'POINTS'] as const
export type AssignableCurrencyType = (typeof ASSIGNABLE_CURRENCY_TYPES)[number]

export function parseCurrencyType(raw: unknown): CurrencyType {
  const s = String(raw ?? '').trim().toUpperCase()
  // POINT@1 is treated as POINTS for share + money (Strix POINTS tree).
  if (s === 'POINTS' || s === 'POINT' || s === 'POINT@1' || s === 'POINT1' || s === 'POINT_AT_1') {
    return 'POINTS'
  }
  return 'INR'
}

/** 1 POINTS = 100 INR = 10_000 paise. POINT@1 = 1 INR = 100 paise. */
function paisePerDisplay(c: CurrencyType): number {
  if (c === 'POINTS') return 10_000
  if (c === 'POINT@1') return 100
  return 100
}

export function displayToPaise(display: number, currency: CurrencyType): string {
  if (!Number.isFinite(display) || display < 0) return '0'
  return String(Math.round(display * paisePerDisplay(currency)))
}

export function paiseToDisplay(minor: string | null | undefined, currency: CurrencyType): number {
  if (minor == null || minor === '') return 0
  const n = Number(minor)
  if (!Number.isFinite(n) || n < 0) return 0
  return n / paisePerDisplay(currency)
}

/** Signed minor → display (Lab balanceUp / posted P&L can be negative). */
export function signedPaiseToDisplay(
  minor: string | null | undefined,
  currency: CurrencyType,
): number {
  if (minor == null || minor === '') return 0
  const n = Number(minor)
  if (!Number.isFinite(n)) return 0
  return n / paisePerDisplay(currency)
}

/** Order/book INR rupees → agent display (POINTS: 1 pt = ₹100). */
export function inrMajorToDisplay(inrMajor: number, currency: CurrencyType): number {
  if (!Number.isFinite(inrMajor)) return 0
  if (currency === 'POINTS') return inrMajor / 100
  return inrMajor
}

export function currencyLabel(c: CurrencyType): string {
  if (c === 'POINTS') return 'Points (1 = ₹100)'
  if (c === 'POINT@1') return 'Point@1 (1 = ₹1)'
  return 'INR'
}

/** CM console: set currency on TM or a direct member. TM downline inherits. */
export function canPickCurrency(role: Role | null | undefined): boolean {
  return role === 'triple-master' || role === 'player'
}

/**
 * Lab create-member: Casino Currency Flag when CompanyMaster is logged in
 * (`accessLevel == 'CompanyMaster'`), on create and edit — not only for TM subject.
 * Mid-level roles cannot change casino currency (Lab / SHARKS-109).
 */
export function canEditCasinoCurrency(opts: {
  actorLevel?: number | null
  subjectRole?: Role | null
}): boolean {
  return opts.actorLevel === 1
}

/** Lab casinoCurrencyType — CM→TM / CM→member create or edit. */
export function casinoCurrencyCreateFields(
  role: Role | null | undefined,
  casinoCurrencyType: 'HKD' | 'AED',
  currencyType?: CurrencyType | string | null,
): { casinoCurrencyType: 'HKD' | 'AED' } | Record<string, never> {
  if (!canPickCurrency(role)) return {}
  return {
    casinoCurrencyType: resolveCasinoCurrencyForSportsbook(
      currencyType,
      casinoCurrencyType,
    ),
  }
}

/** Lab fundistCurrencyType — SoftGamings Crash Games (CM→TM / CM→member). */
export type FundistCurrencyType = 'HKD' | 'AED' | 'USD' | 'SGD' | 'INR'

export const FUNDIST_CURRENCY_TYPES: readonly FundistCurrencyType[] = [
  'HKD',
  'AED',
  'USD',
  'SGD',
  'INR',
] as const

export function parseFundistCurrencyType(
  raw: unknown,
  fallback: FundistCurrencyType = 'HKD',
): FundistCurrencyType {
  const s = String(raw ?? '')
    .trim()
    .toUpperCase()
  if ((FUNDIST_CURRENCY_TYPES as readonly string[]).includes(s)) {
    return s as FundistCurrencyType
  }
  return fallback
}

export function fundistCurrencyCreateFields(
  role: Role | null | undefined,
  fundistCurrencyType: FundistCurrencyType,
): { fundistCurrencyType: FundistCurrencyType } | Record<string, never> {
  if (!canPickCurrency(role)) return {}
  return { fundistCurrencyType: parseFundistCurrencyType(fundistCurrencyType) }
}

/** INR trees lock Evolution to HKD; POINTS may pick HKD|AED. */
export function casinoCurrencyOptionsForSportsbook(
  currencyType?: CurrencyType | string | null,
): readonly ('HKD' | 'AED')[] {
  return isInrSportsbook(currencyType) ? (['HKD'] as const) : (['HKD', 'AED'] as const)
}

export function resolveCasinoCurrencyForSportsbook(
  currencyType: CurrencyType | string | null | undefined,
  requested: unknown,
  fallback: 'HKD' | 'AED' = 'HKD',
): 'HKD' | 'AED' {
  if (isInrSportsbook(currencyType)) return 'HKD'
  return parseCasinoCurrencyType(requested, fallback)
}

function isInrSportsbook(raw?: string | null): boolean {
  return (
    String(raw ?? '')
      .trim()
      .toUpperCase() === 'INR'
  )
}

/** Lab create-member Site URL — required when CM creates TM. */
const SITE_URL_RE =
  /^((https?):\/\/)?(www\.)?[a-zA-Z0-9_-]+(\.[a-zA-Z0-9_-]+)+([/?#].*)?$/i

export function siteUrlError(raw: string): string | null {
  const s = raw.trim()
  if (!s) return 'Site URL is required'
  if (!SITE_URL_RE.test(s)) return 'Please enter a valid Site URL'
  return null
}

/** CM→TM business channel (UI). Maps to API `isB2C`. */
export const BUSINESS_TYPES = ['B2B', 'B2C'] as const
export type BusinessType = (typeof BUSINESS_TYPES)[number]

/** CM→TM B2C category tokens persisted as `b2cCategory`. */
export const B2C_CATEGORIES = ['self-deposit', 'book-system', 'both'] as const
export type B2cCategory = (typeof B2C_CATEGORIES)[number]

export const B2C_CATEGORY_LABEL: Record<B2cCategory, string> = {
  'self-deposit': 'Self deposit',
  'book-system': 'Book system',
  both: 'Both',
}

export function parseBusinessType(raw: unknown, fallback: BusinessType = 'B2B'): BusinessType {
  const s = String(raw ?? '')
    .trim()
    .toUpperCase()
  return s === 'B2C' ? 'B2C' : s === 'B2B' ? 'B2B' : fallback
}

export function parseB2cCategory(raw: unknown): B2cCategory | null {
  const s = String(raw ?? '')
    .trim()
    .toLowerCase()
  return (B2C_CATEGORIES as readonly string[]).includes(s) ? (s as B2cCategory) : null
}

export function b2cCategoryError(businessType: BusinessType, raw: string): string | null {
  if (businessType !== 'B2C') return null
  if (!raw.trim()) return 'Select a B2C category'
  if (!parseB2cCategory(raw)) return 'Select Self deposit, Book system, or Both'
  return null
}

/** Create/update payload fields for CM→TM B2B/B2C. */
export function b2cCreateFields(
  businessType: BusinessType,
  b2cCategory: string,
): { isB2C: boolean; b2cCategory?: B2cCategory } {
  if (businessType !== 'B2C') return { isB2C: false }
  const cat = parseB2cCategory(b2cCategory)
  return cat ? { isB2C: true, b2cCategory: cat } : { isB2C: true }
}

/** Map stored isB2C onto the Business type dropdown. */
export function businessTypeFromIsB2C(isB2C: unknown): BusinessType {
  return isB2C === true ? 'B2C' : 'B2B'
}

export function parseCasinoCurrencyType(
  raw: unknown,
  fallback: 'HKD' | 'AED' = 'HKD',
): 'HKD' | 'AED' {
  const s = String(raw ?? '')
    .trim()
    .toUpperCase()
  if (s === 'HKD' || s === 'AED') return s
  return fallback
}

/** Paise string → ₹ major. Null/missing → 0 (prefer 0 over dash). */
export function fromMinorUnits(minor: string | null | undefined): number {
  return paiseToDisplay(minor, 'INR')
}

/** ₹ major → paise string. */
export function toMinorUnits(major: number): string {
  return displayToPaise(major, 'INR')
}

/** Share UI value → basis points (SportsBook).
 * INR Member Rate 0–1 → bp = rate × 10000 (Lab maxRate 1).
 * POINTS My share any integer % → bp = points × 100 (uncapped).
 */
export function sharePointsToBp(
  points: number,
  currency: CurrencyType = 'POINTS',
): number {
  if (!Number.isFinite(points) || points < 0) return 0
  if (currency === 'INR') return Math.min(10_000, Math.round(points * 10_000))
  return Math.round(points * 100)
}

/**
 * Lab Live Casino Member Rate → basis points (always % × 100).
 * INR UI is 0–100; POINTS / POINT@1 accept any finite number.
 */
export function casinoSharePointsToBp(points: number): number {
  if (!Number.isFinite(points)) return 0
  return Math.round(points * 100)
}

/** Commission % (e.g. 0.96) → basis points. */
export function commissionPercentToBp(percent: number): number {
  if (!Number.isFinite(percent) || percent < 0) return 0
  return Math.min(10_000, Math.round(percent * 100))
}

export function bpToSharePoints(bp: number, currency: CurrencyType = 'POINTS'): number {
  if (currency === 'INR') return (bp ?? 0) / 10_000
  return (bp ?? 0) / 100
}

/** Live Casino bp → UI % (Lab memberRatePercent 0–100 scale). */
export function bpToCasinoSharePoints(bp: number): number {
  return (bp ?? 0) / 100
}

/**
 * Lab sevenone-1 SportsBook:
 * - INR: absolute Member Rate (0–1); child ≥ parent; player leaf 10000 bp
 * - POINTS: own-keep My share (any integer ≥ 0); no fall-down floor; no ≤100 cap
 */
export function maxShareOf(
  _yourShare: number,
  currency: CurrencyType = 'POINTS',
): number | null {
  return currency === 'INR' ? 1 : null
}

/** Live Casino: INR capped at 100; POINTS / POINT@1 have no UI ceiling. */
export function maxCasinoShareOf(currency: CurrencyType = 'POINTS'): number | null {
  return currency === 'INR' ? 100 : null
}

export function minShareOf(
  yourShare: number,
  currency: CurrencyType = 'POINTS',
): number {
  if (currency !== 'INR') return 0
  const max = maxShareOf(yourShare, currency) ?? 1
  const p = Number(yourShare)
  if (!Number.isFinite(p) || p < 0) return 0
  return Math.min(max, p)
}

/** Live Casino has no parent floor (Lab skips INR Live Casino ≥ parent/100). */
export function minCasinoShareOf(_currency: CurrencyType = 'POINTS'): number {
  return 0
}

/** Lab INR SportsBook step; POINTS uses whole percents. */
export function shareStep(currency: CurrencyType): number {
  return currency === 'INR' ? 0.01 : 1
}

/** Live Casino form step — whole % for INR; fine step for Point currencies. */
export function casinoShareStep(currency: CurrencyType): number {
  return currency === 'INR' ? 1 : 0.01
}

/** Review / list display — 2dp for INR so 0.05 does not become 0.1. */
export function formatSharePoints(points: number, currency: CurrencyType): string {
  if (!Number.isFinite(points)) return '—'
  return currency === 'INR' ? points.toFixed(2) : points.toFixed(0)
}

export function formatCasinoSharePoints(points: number, currency: CurrencyType): string {
  if (!Number.isFinite(points)) return '—'
  return currency === 'INR' ? points.toFixed(0) : String(points)
}

/** INR: My Share = entered − parent. POINTS: textbox is the keep. */
export function myShareKeep(
  entered: number,
  parentRate: number,
  currency: CurrencyType = 'POINTS',
): number {
  if (!Number.isFinite(entered)) return 0
  if (currency === 'INR') {
    if (!Number.isFinite(parentRate)) return 0
    return Math.max(0, entered - parentRate)
  }
  return Math.max(0, entered)
}

/**
 * Live Casino Member Rate validation (Lab create/edit):
 * - POINTS / POINT@1: any finite numeric value
 * - INR: 0–100 inclusive
 */
export function casinoShareError(
  value: number | null,
  currency: CurrencyType = 'POINTS',
): string | null {
  const kind = currency === 'INR' ? 'casino Member Rate' : 'casino My share'
  if (value === null || !Number.isFinite(value)) return `Enter a ${kind}`
  if (currency === 'INR') {
    if (value < 0 || value > 100) return `${kind} must be between 0 and 100`
  }
  return null
}

export function shareError(
  value: number | null,
  yourShare: number,
  label: 'sportsbook' | 'casino' = 'sportsbook',
  currency: CurrencyType = 'POINTS',
): string | null {
  if (label === 'casino') return casinoShareError(value, currency)
  const kind = currency === 'INR' ? 'Member Rate' : 'My share'
  const max = maxShareOf(yourShare, currency)
  const min = minShareOf(yourShare, currency)
  if (value === null) return `Enter a ${kind}`
  if (value < 0) return 'A share cannot be negative'
  if (currency === 'INR') {
    if (min <= 1e-9 && value <= 1e-9) return 'please enter my share greater than 0'
    if (value + 1e-9 < min) {
      return `${kind} must be at least ${min} (upline rate — Lab fall-down)`
    }
    if (max != null && value > max + 1e-9) return `${kind} must be at most ${max}`
    return null
  }
  // POINTS: any integer ≥ 0 (CM=0; player stores Master's My Share).
  if (!Number.isInteger(value)) return `${kind} must be a whole number`
  return null
}

export function isMemberRole(role: Role | null | undefined): boolean {
  return role === 'player'
}

export function isMemberLevel(level: number | null | undefined): boolean {
  return level === MEMBER_LEVEL
}

/**
 * Partnership share for create / update.
 * INR members forced to SportsBook leaf 10000 bp.
 * POINTS members keep Master's entered My Share on the player.
 * Casino: INR leaf 10000; POINTS keeps entered My share.
 */
export function sharePayloadBp(input: {
  isMember: boolean
  sportsbookPoints: number
  casinoPoints: number
  currency: CurrencyType
}): { shareBp: number; shareCasinoBp: number } {
  if (input.isMember && input.currency === 'INR') {
    return { shareBp: 10_000, shareCasinoBp: 10_000 }
  }
  if (input.isMember) {
    return {
      shareBp: sharePointsToBp(input.sportsbookPoints, input.currency),
      shareCasinoBp: casinoSharePointsToBp(input.casinoPoints),
    }
  }
  return {
    shareBp: sharePointsToBp(input.sportsbookPoints, input.currency),
    shareCasinoBp: casinoSharePointsToBp(input.casinoPoints),
  }
}

/**
 * Share to display for a node.
 * INR members: SportsBook leaf 100% / Casino 100.
 * POINTS members: stored My Share (Master keep on player).
 */
export function nodeSharePoints(node: {
  level: number
  shareBp: number
  shareCasinoBp?: number
  currencyType?: string
}): { sportsbook: number; casino: number } {
  const ccy = parseCurrencyType(node.currencyType)
  if (isMemberLevel(node.level) && ccy === 'INR') {
    return { sportsbook: 1, casino: 100 }
  }
  if (isMemberLevel(node.level)) {
    return {
      sportsbook: bpToSharePoints(node.shareBp, ccy),
      casino: bpToCasinoSharePoints(node.shareCasinoBp ?? node.shareBp),
    }
  }
  return {
    sportsbook: bpToSharePoints(node.shareBp, ccy),
    casino: bpToCasinoSharePoints(node.shareCasinoBp ?? node.shareBp),
  }
}

export function bpToCommissionPercent(bp: number): number {
  return (bp ?? 0) / 100
}

export function formatInr(major: number): string {
  return formatMoney(major, 'INR')
}

export function formatMoney(display: number, currency: CurrencyType = 'INR'): string {
  if (!Number.isFinite(display)) return '—'
  const n = new Intl.NumberFormat('en-IN', {
    maximumFractionDigits: 2,
  }).format(display)
  if (currency === 'INR') {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2,
    }).format(display)
  }
  return `${n} Pts`
}

export function formatNodeMoney(
  minor: string | null | undefined,
  currencyRaw?: string | null,
): string {
  const currency = parseCurrencyType(currencyRaw)
  return formatMoney(paiseToDisplay(minor, currency), currency)
}

export type HierarchyNode = {
  id: string
  parentId: string | null
  name: string
  level: number
  creditLimit: string
  shareBp: number
  shareCasinoBp?: number
  commissionBp?: number
  commissionBookmakerBp?: number
  commissionMatchOddsBp?: number
  /** Lab Member.rate[] (Info) — from hierarchy tree when Agent-API ships it. */
  rate?: LabRateRow[]
  games?: {
    casino?: boolean
    cardGame?: boolean
    liveGame?: boolean
    sportsbook?: boolean
    premiumSportsbook?: boolean
    horseRacing?: boolean
    matchOdds?: boolean
    bookmaker?: boolean
    customSession?: boolean
    completedMatch?: boolean
    tiedMatch?: boolean
    lineSession?: boolean
    cricket?: boolean
    uofMarkets?: boolean
    esports?: boolean
    greyhoundRacing?: boolean
    darts?: boolean
    election?: boolean
    tennis?: boolean
    soccer?: boolean
    soccerSuper?: boolean
    sportHorseRacing?: boolean
    volleyball?: boolean
    snooker?: boolean
    rugbyLeague?: boolean
    artemis?: boolean
    rugbyUnion?: boolean
    googleAuthenticator?: boolean
  }
  gameLocks?: Partial<
    Record<
      | 'sportsbook'
      | 'casino'
      | 'card'
      | 'liveGame'
      | 'premiumSportsbook'
      | 'horseRacing'
      | 'matchOdds'
      | 'bookmaker'
      | 'customSession'
      | 'completedMatch'
      | 'tiedMatch'
      | 'lineSession'
      | 'transfer',
      { status: boolean; level: number }
    >
  >
  betMin?: string | null
  betMax?: string | null
  status?: string
  comments?: string
  balance?: string
  exposure?: string
  exposureStale?: boolean
  /** Lab Balance product NE (paise) — players only, from Agent-API get. */
  exposureSportsbook?: string
  exposureLiveCasino?: string
  exposureLiveCards?: string
  exposurePremiumBm?: string
  exposureHorseRacing?: string
  available?: string
  creditRemaining?: string
  liveBalance?: string
  totalBalance?: string
  distributedBalance?: string
  /** Lab currencyType (INR / POINTS). Inherited from TM. */
  currencyType?: string
  /** Lab casinoCurrencyType — Evolution HKD|AED. */
  casinoCurrencyType?: string
  /** Lab fundistCurrencyType — SoftGamings Crash Games. */
  fundistCurrencyType?: string
  /** Lab siteUrl — white-label hostname (CM→TM). */
  siteUrl?: string
  /** CM→TM: false = B2B, true = B2C. */
  isB2C?: boolean
  /** When isB2C: self-deposit | book-system | both. */
  b2cCategory?: string
  remainingToAssign?: string
  username?: string
  /** Lab balanceUp / Winnings (signed settlement P&L, minor). */
  postedPnl?: string
  /**
   * Winner7 Super/Master/Player max CL cascade — level-keyed minor units
   * (`{ "2": "…", "4": "…" }`) from Agent-API `downlineMaxCredit`.
   */
  downlineMaxCredit?: Record<string, string>
  /** Lab globalLock — CM switch on self, effective row lock on tree children. */
  globalLock?: boolean
  /** Lab unLockedMembers on the locker node. */
  unlockedMemberIds?: string[]
}

/** Lab Mongo Game ids (Info / PL filters). */
export const LAB_GAME_IDS = {
  sportsBook: '5b36053e46123555cca93993',
  liveCasino: '5a818572650cb46dd9b1a25a',
} as const

export type LabRateRow = {
  _id: string
  game: { _id: string; name: string }
  parentRatePercent: number
  memberRatePercent: number
  memberSharePercentage: number
  parentSharePercentage: number
  memberAssignedRate: number
  maxRate: number
}

function bpToLabSportsbookFraction(bp: number): number {
  const n = Number(bp)
  if (!Number.isFinite(n) || n <= 0) return 0
  return Math.min(1, n / 10_000)
}

/**
 * Lab Info rate[] from share columns — used when tree omits `rate`
 * (older hierarchy) or parent shares unknown (parent → 0).
 * Live Casino uses 0–100 memberRatePercent; SportsBook INR uses 0–1.
 */
export function labRateForNode(
  node: Pick<HierarchyNode, 'id' | 'shareBp' | 'shareCasinoBp' | 'rate'>,
  parent?: Pick<HierarchyNode, 'shareBp' | 'shareCasinoBp'> | null,
): LabRateRow[] {
  if (Array.isArray(node.rate) && node.rate.length > 0) return node.rate
  const shareBp = Number(node.shareBp ?? 0)
  const shareCasinoBp = Number(node.shareCasinoBp ?? shareBp)
  const parentSb = Number(parent?.shareBp ?? 0)
  const parentCasino = Number(parent?.shareCasinoBp ?? parentSb)
  const sbMember = bpToLabSportsbookFraction(shareBp)
  const sbParent = bpToLabSportsbookFraction(parentSb)
  const casinoMember = bpToCasinoSharePoints(shareCasinoBp)
  const casinoParent = bpToCasinoSharePoints(parentCasino)
  return [
    {
      _id: `${node.id}:live-casino`,
      game: { _id: LAB_GAME_IDS.liveCasino, name: 'Live Casino' },
      parentRatePercent: casinoParent,
      memberRatePercent: casinoMember,
      memberSharePercentage: casinoMember / 100,
      parentSharePercentage: casinoParent / 100,
      memberAssignedRate: 0,
      maxRate: 100,
    },
    {
      _id: `${node.id}:sportsbook`,
      game: { _id: LAB_GAME_IDS.sportsBook, name: 'SportsBook' },
      parentRatePercent: sbParent,
      memberRatePercent: sbMember,
      memberSharePercentage: sbMember,
      parentSharePercentage: sbParent,
      memberAssignedRate: 0,
      maxRate: 100,
    },
  ]
}

/** Info popup value — Lab INR uses memberRatePercent; POINTS uses share×100. */
export function labRateDisplayValue(
  rate: LabRateRow,
  currencyRaw?: string | null,
): number {
  const ccy = parseCurrencyType(currencyRaw)
  if (ccy === 'INR') return Number(rate.memberRatePercent ?? 0)
  return Number(rate.memberSharePercentage ?? 0) * 100
}

/** Hidden from Member List Rate Info (Lab Poker/Roulette + sheet #31 drop Club Casino). */
const RATE_INFO_HIDDEN_GAMES = new Set(['Poker', 'Roulette', 'TeenPatti', 'Club Casino'])

export function labRateRowsForInfo(
  node: HierarchyNode,
  parent?: HierarchyNode | null,
): LabRateRow[] {
  return labRateForNode(node, parent).filter((r) => {
    const n = r.game?.name ?? ''
    return !RATE_INFO_HIDDEN_GAMES.has(n)
  })
}

/** Locked Username field on edit — never use display name as a fallback. */
export function lockedLoginUsername(node: { username?: string } | null | undefined): string {
  return node?.username?.trim() || ''
}

/**
 * Lab Lock column / padlocks: only **Closed** is dead.
 * Active and Inactive stay clickable — Lab wrongly hid Inactive + already-locked
 * (`status == 'Active' && lock.status`); sheet: Lock always available when inactive.
 */
export function hierarchyLockUiBlocked(status: string | null | undefined): boolean {
  return String(status ?? '').trim().toLowerCase() === 'closed'
}

export type HierarchySelf = {
  ok: true
  node: HierarchyNode
  availableCredit: string
  allocatedCredit: string
  money?: {
    liveBalance: string
    totalBalance: string
    distributedBalance: string
    remainingToAssign: string
    balance: string
    creditLimit: string
    exposure: string
    exposureStale?: boolean
    available: string
    creditRemaining: string
  }
}

export type HierarchyTreeResult = {
  ok: true
  rootId: string
  nodes: HierarchyNode[]
}

export type DownlineListScope = 'direct' | 'all'

export function idsEqual(a?: string | null, b?: string | null): boolean {
  return !!a && !!b && a.toLowerCase() === b.toLowerCase()
}

/**
 * Direct child of the logged-in CM.
 *
 * The tree payload sometimes omits the CM row and lists TMs at depth 0 with
 * `parentId: null`. Those still count as direct children.
 */
export function isDirectChildOf(
  node: { id: string; parentId: string | null },
  rootId: string | null | undefined,
  depth?: number,
): boolean {
  if (!rootId || idsEqual(node.id, rootId)) return false
  if (idsEqual(node.parentId, rootId)) return true
  if (depth === 0 && (node.parentId == null || node.parentId === '')) return true
  return false
}

/** Where TreeRows should start: CM's parent (null) if CM is in the list, else CM id. */
export function treeStartParentId(
  nodes: HierarchyNode[],
  rootId: string | null | undefined,
): string | null {
  if (!rootId) return null
  if (nodes.some((n) => idsEqual(n.id, rootId))) return null
  if (nodes.some((n) => idsEqual(n.parentId, rootId))) return rootId
  return null
}

/** Flat list rows for the old-flow downline table (excludes the CM root). */
export function downlineListRows(
  nodes: HierarchyNode[],
  rootId: string | undefined,
  scope: DownlineListScope,
): HierarchyNode[] {
  if (!rootId) return []
  const kids = nodes.filter((n) => !idsEqual(n.id, rootId))
  if (scope === 'direct') return kids.filter((n) => isDirectChildOf(n, rootId, n.parentId == null ? 0 : 1))
  return kids
}

export function filterDownlineRows(
  rows: HierarchyNode[],
  opts: { q?: string; role?: Role | 'all'; settlement?: 'collect' | 'pay' | 'all' },
): HierarchyNode[] {
  const q = (opts.q ?? '').trim().toLowerCase()
  const role = opts.role ?? 'all'
  const settlement = opts.settlement ?? 'all'
  return rows.filter((n) => {
    if (role !== 'all' && levelToRole(n.level) !== role) return false
    if (settlement === 'collect') {
      // Lab Take column: balanceUp < 0 (child owes / parent collects)
      if (!(Number(n.postedPnl ?? '0') < 0)) return false
    } else if (settlement === 'pay') {
      // Lab Give column: balanceUp > 0 (parent pays winning child)
      if (!(Number(n.postedPnl ?? '0') > 0)) return false
    }
    if (!q) return true
    const hay = `${n.name} ${n.username ?? ''} ${ROLE_LABEL[levelToRole(n.level)]}`.toLowerCase()
    return hay.includes(q)
  })
}

/**
 * Lab Member List Take / Give from balanceUp (`postedPnl`).
 * Take when child is losing (pnl < 0); Give when child is winning (pnl > 0).
 * Amounts are in the child's display currency.
 */
export function settleFromPostedPnl(
  postedPnlMinor: string | null | undefined,
  currencyRaw?: string | null,
): { direction: 'give' | 'take'; amountDisplay: number; amountMinor: string } | null {
  const currency = parseCurrencyType(currencyRaw)
  const pnl = Number(postedPnlMinor ?? '0')
  if (!Number.isFinite(pnl) || pnl === 0) return null
  if (pnl < 0) {
    const amountDisplay = signedPaiseToDisplay(String(-pnl), currency)
    return {
      direction: 'take',
      amountDisplay,
      amountMinor: String(Math.round(-pnl)),
    }
  }
  const amountDisplay = signedPaiseToDisplay(String(pnl), currency)
  return {
    direction: 'give',
    amountDisplay,
    amountMinor: String(Math.round(pnl)),
  }
}

/** Floor to display paise (2dp) — never round up past a ledger cap. */
export function floor2(n: number): number {
  if (!Number.isFinite(n) || n <= 0) return 0
  return Math.floor(n * 100 + 1e-9) / 100
}

export function round2(n: number): number {
  if (!Number.isFinite(n)) return 0
  return Math.round(n * 100 + Number.EPSILON) / 100
}

export type TransferCapInfo = {
  /** Max movable in the child's display currency. */
  capDisplay: number
  /** Sentence under the amount field — always names the constraint. */
  because: string
  /** True when capping to Lab Take/Give outstanding (P&L settle, no TB required). */
  settlement: boolean
}

/**
 * Cap for TransferDialog — Lab / Backend-UI parity.
 * Outstanding Take/Give: full column (no cash / credit-limit headroom).
 * Cash-only: take ≤ child live balance; give ≤ parent live balance.
 */
export function transferCapFor(
  account: HierarchyNode,
  direction: 'give' | 'take',
  selfLiveDisplay = 0,
): TransferCapInfo {
  const currency = parseCurrencyType(account.currencyType)
  const name = account.username?.trim() || account.name
  const fmt = (d: number) => formatMoney(d, currency)
  const outstanding = settleFromPostedPnl(account.postedPnl, account.currencyType)

  if (direction === 'take') {
    if (outstanding?.direction === 'take') {
      const cap = floor2(outstanding.amountDisplay)
      return {
        capDisplay: cap,
        because: `Up to ${fmt(cap)} — Take outstanding for ${name} (settlement; no cash required).`,
        settlement: true,
      }
    }
    const bal = paiseToDisplay(account.liveBalance ?? account.balance, currency)
    const cap = Math.max(0, floor2(bal))
    return {
      capDisplay: cap,
      because:
        cap <= 0
          ? `${name} holds ${fmt(bal)}, so there is nothing to take.`
          : `Up to ${fmt(cap)} — everything ${name} currently holds.`,
      settlement: false,
    }
  }

  if (outstanding?.direction === 'give') {
    const cap = floor2(outstanding.amountDisplay)
    return {
      capDisplay: cap,
      because: `Up to ${fmt(cap)} — Give outstanding for ${name} (settlement; no cash required).`,
      settlement: true,
    }
  }
  const free = Math.max(0, floor2(selfLiveDisplay))
  return {
    capDisplay: free,
    because:
      free <= 0
        ? `Nothing can be given right now: you have ${fmt(free)} available.`
        : `Up to ${fmt(free)} — your live balance available to assign.`,
    settlement: false,
  }
}

/** Server returns raw paise in "allowed max is N" — show display money instead. */
export function humanizeCreditTransferError(
  message: string,
  currencyRaw?: string | null,
): string {
  return message.replace(/allowed max is (\d+)/gi, (_full, minor: string) => {
    return `allowed max is ${formatNodeMoney(minor, currencyRaw)}`
  })
}

/** When raising credit on a child, ceiling = parent remaining + child's current limit. */
export function editCreditCeiling(
  parentAvailableMajor: number,
  childCreditLimitMajor: number,
  /** Parent `downlineMaxCredit` for this child level (major). */
  parentLevelCapMajor?: number | null,
): number {
  const a = Number.isFinite(parentAvailableMajor) ? Math.max(0, parentAvailableMajor) : 0
  const c = Number.isFinite(childCreditLimitMajor) ? Math.max(0, childCreditLimitMajor) : 0
  const remainingPlusCurrent = a + c
  if (
    parentLevelCapMajor == null ||
    !Number.isFinite(parentLevelCapMajor) ||
    parentLevelCapMajor <= 0
  ) {
    return remainingPlusCurrent
  }
  return Math.min(remainingPlusCurrent, parentLevelCapMajor)
}

/** Parent downline-max for a child level → major display units (or null). */
export function parentDownlineMaxMajor(
  parentCaps: Record<string, string> | undefined,
  childLevel: number,
  currency: CurrencyType,
): number | null {
  if (!parentCaps) return null
  const raw = parentCaps[String(childLevel)]
  if (raw == null || raw === '') return null
  const major = paiseToDisplay(raw, currency)
  return major > 0 ? major : null
}
